import type { Notification } from '@/types/notification';

export type NotificationType = Notification['type'];

const NOTIFICATION_ICON_MAP: Record<NotificationType, string> = {
  like: 'heart',
  comment: 'chatbubble',
  follow: 'person-add',
  mention: 'at',
  friend_request: 'person-add',
  friend_request_accepted: 'checkmark-circle',
  list_invitation: 'list',
  list_invitation_accepted: 'checkmark-circle',
  list_item_added: 'bookmark',
  friend_recommendation: 'paper-plane-outline',
};

const NOTIFICATION_ICON_COLOR_MAP: Record<NotificationType, string> = {
  like: 'text-coral',
  comment: 'text-muted-foreground',
  follow: 'text-info',
  mention: 'text-warning',
  friend_request: 'text-info',
  friend_request_accepted: 'text-success',
  list_invitation: 'text-muted-foreground',
  list_invitation_accepted: 'text-success',
  list_item_added: 'text-primary',
  friend_recommendation: 'text-muted-foreground',
};

const NOTIFICATION_BADGE_BG_MAP: Record<NotificationType, string> = {
  like: 'bg-coral',
  comment: 'bg-muted-foreground',
  follow: 'bg-info',
  mention: 'bg-warning',
  friend_request: 'bg-info',
  friend_request_accepted: 'bg-success',
  list_invitation: 'bg-muted-foreground',
  list_invitation_accepted: 'bg-success',
  list_item_added: 'bg-primary',
  friend_recommendation: 'bg-primary',
};

export function getNotificationIcon(type: string): string {
  return NOTIFICATION_ICON_MAP[type as NotificationType] ?? 'notifications';
}

export function getNotificationIconColor(type: string): string {
  return NOTIFICATION_ICON_COLOR_MAP[type as NotificationType] ?? 'text-muted-foreground';
}

export function getNotificationBadgeBg(type: string): string {
  return NOTIFICATION_BADGE_BG_MAP[type as NotificationType] ?? 'bg-muted-foreground';
}

export function isCheckInLike(notification: Notification): boolean {
  return (
    notification.type === 'like' &&
    notification.referenceType === 'check_in' &&
    !!notification.menuItemName &&
    !!notification.businessName
  );
}

export function isFriendRecommendation(notification: Notification): boolean {
  return (
    notification.type === 'friend_recommendation' &&
    !!notification.menuItemName &&
    !!notification.businessName
  );
}

export function isCheckInComment(notification: Notification): boolean {
  return (
    notification.type === 'comment' &&
    notification.referenceType === 'check_in' &&
    !!notification.menuItemName &&
    !!notification.businessName
  );
}

export function hasCheckInPreview(notification: Notification): boolean {
  return (
    (notification.type === 'like' || notification.type === 'comment') &&
    notification.referenceType === 'check_in' &&
    !!notification.menuItemName &&
    !!notification.businessName &&
    !!notification.referenceId &&
    !!notification.checkInUserName
  );
}

export function hasMenuItemPreview(notification: Notification): boolean {
  return (
    notification.referenceType === 'menu_item' &&
    !!notification.menuItemName &&
    !!notification.businessName &&
    !!notification.referenceId
  );
}

export function hasListPreview(notification: Notification): boolean {
  return (
    notification.referenceType === 'list' &&
    !!notification.listId &&
    !!notification.listName
  );
}
